import React from "react";

import cn from "classnames";

import { TEXT_DISPLAY_NAME } from "./utils/core";
import { boxForwardRef } from "../../../helpers/common/Box/Box";
import TextCore from "../../../helpers/common/TextCore/TextCore";

import type { BoxComponentWithDefault } from "../../../helpers/common/Box/Box";
import type {
    TextCorePropsMultiLine,
    TextCorePropsOneLine,
} from "../../../helpers/common/TextCore/types";
import type {
    PropsWithCustomVarsStyles,
    PropsWithTsId,
} from "../../../types/react";

import classes from "./Text.styl";

export const TEXT_TEST_ID = "text-test-id";

export type TextProps = PropsWithCustomVarsStyles<
    | Omit<TextCorePropsOneLine, "textClassName" | "textContainerClassName">
    | Omit<TextCorePropsMultiLine, "textClassName" | "textContainerClassName">
> &
    Partial<PropsWithTsId>;

export type PolymorphicTextProps = BoxComponentWithDefault<
    "span",
    TextProps,
    "className" | "style"
>;

/**
 * Компонент, представляющий собой строчку число-буквенных символов,
 * который можно использовать как самостоятельный элемент
 * и в составе других компонентов для гибкой настройки строки и переиспользования ряда параметров
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=10744-60845" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<Text.Item />` | Полиморфный компонент.<br/> Нужен для прокидывания дочерних текстовых компонентов в родительский. Фактически тот же &lt;Text /&gt;, у которого убраны пропы `truncateStyle` и `maxLines`. |
 * | `<Text.Link />` | Полиморфный компонент.<br/> Ссылка, наследующая типографику родительского &lt;Text /&gt;. Поддерживает пропы view и customColorsStyles для настройки цветов. При необходимости использовать только ссылку, используй просто `<TextLink />` без &lt;Text /&gt;. |
 * | `<Text.CounterInline />` | Счетчик, по умолчанию наследующий типографику родительского &lt;Text /&gt; |
 * | `<Text><Icon /></Text>` | Компонент умеет принимать &lt;Icon /&gt; |
 *
 * @constructor
 */
export const Text = boxForwardRef<PolymorphicTextProps>(
    ({ children, "data-tsid": tsid, customVarsStyles, ...props }, ref) => {
        const textCustomVarsStyles = cn(
            classes["text-custom-vars"],
            customVarsStyles,
        );

        return (
            <TextCore
                data-tsid={tsid || TEXT_TEST_ID}
                data-uikit={TEXT_DISPLAY_NAME}
                {...props}
                customVarsStyles={customVarsStyles && textCustomVarsStyles}
                ref={ref}
            >
                {children}
            </TextCore>
        );
    },
);

Text.displayName = TEXT_DISPLAY_NAME;

export default Text;
